import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { capitalizeFirstLetters } from '../../utils'
import { presets } from './productPresets'
import FormPage from '../../components/FormPage'
import ActionBar from '../../components/ActionBar'
import Lightbox from '../../components/Lightbox'
import { useBlockingSpan } from '../../loading/BlockingLoad'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import InputAdornment from '@mui/material/InputAdornment'

const { categories } = presets

const WATCH_CATEGORIES = categories.filter(category => category.toLowerCase().includes('watch'))

const FORM_MEASURE = 536

/** Long enough that a burst of typing is one request; short enough to feel immediate. */
const LOOKUP_DEBOUNCE_MS = 400

const defaultEntry = { cost: '', price: '', nwk_quantity: 0, lb_quantity: 0, weight: '' }

export default function AddSeikoWatch({ showAddSeiko, setShowAddSeiko, fetchResource, refreshProducts, onExistingPiece }) {
    const { t } = useTranslation()
    const runBlocking = useBlockingSpan()

    const [model, setModel] = useState('')
    const [lookup, setLookup] = useState(null)
    const [searching, setSearching] = useState(false)
    const [lookupFailed, setLookupFailed] = useState(false)
    // The clerk has said the number is right despite the catalogue not knowing it. Only
    // then do the two derived fields appear -- a miss is usually a typo, and the point of
    // saying so first is that it gets caught here rather than on the storefront.
    const [confirmedUnknown, setConfirmedUnknown] = useState(false)
    const [fallback, setFallback] = useState({ category: '', name: '' })
    const [entry, setEntry] = useState(defaultEntry)
    const [photoOpen, setPhotoOpen] = useState(false)
    const [inStock, setInStock] = useState([])
    const [reporting, setReporting] = useState(false)
    const [reportNote, setReportNote] = useState('')
    const [reported, setReported] = useState(false)

    const requestId = useRef(0)

    useEffect(() => {
        const typed = model.trim()

        // Bumped unconditionally, including the empty branch below: a response from a
        // request that predates a clear must never land and revive the card over a
        // blank field.
        const id = ++requestId.current

        // Cleared on every change, not just when the field empties. Otherwise the card
        // and Save keep describing the previous model for the whole debounce window
        // while the input already shows a different one -- and Save would submit the
        // old model's title, image, contentHash and watch_model.
        setLookup(null)
        setLookupFailed(false)
        setConfirmedUnknown(false)
        setInStock([]); setReporting(false); setReportNote(''); setReported(false)

        if (!typed) {
            setSearching(false)
            return
        }

        setSearching(true)

        const timer = setTimeout(async () => {
            // Fired alongside the lookup, never before it and never gating it. The lookup
            // answers from the committed snapshot instantly and offline; this one asks
            // Shopify. Folding them together would trade the lookup's offline property for
            // an advisory, and make a Shopify blip cost the clerk their card.
            fetchResource({
                method: 'GET',
                resource: `products/seiko-in-stock?model=${encodeURIComponent(typed)}`,
            })
                .then(stock => {
                    if (id !== requestId.current) return
                    setInStock(Array.isArray(stock?.matches) ? stock.matches : [])
                })
                .catch(() => { /* advisory only -- its absence costs the clerk nothing */ })

            try {
                const response = await fetchResource({
                    method: 'GET',
                    resource: `products/seiko-lookup?model=${encodeURIComponent(typed)}`,
                })
                // A slower earlier request must not overwrite a newer answer.
                if (id !== requestId.current) return
                // useFetch's fetchResource never rejects: it catches internally and
                // *resolves* with { error }. So a failed lookup lands here, in the success
                // path, not in the catch below -- and arrives with no `status`. Treat that
                // as the failure case, on the same path as an unknown model: say so, let
                // them go on.
                if (!response?.status) {
                    setLookup(null)
                    setLookupFailed(true)
                } else {
                    setLookup(response)
                    setLookupFailed(false)
                }
            } catch {
                if (id !== requestId.current) return
                // Belt-and-braces: fetchResource is not supposed to throw, but if it ever
                // did, the catalogue being unreachable still isn't a reason to stop taking
                // the watch in.
                setLookup(null)
                setLookupFailed(true)
            } finally {
                if (id === requestId.current) setSearching(false)
            }
        }, LOOKUP_DEBOUNCE_MS)

        return () => clearTimeout(timer)
    }, [model, fetchResource])

    useEffect(() => {
        if (!showAddSeiko) {
            setModel(''); setLookup(null); setEntry(defaultEntry); setPhotoOpen(false)
            setFallback({ category: '', name: '' }); setConfirmedUnknown(false); setLookupFailed(false)
        }
    }, [showAddSeiko])

    const found = lookup?.status === 'found'
    // Two catalogue records (SSH137, SSH139) are `found` with no gender on file, so
    // categoryFor() on the server comes back null even on a match. The clerk is the only
    // remaining source for the category then -- Save must wait on it.
    const needsCategoryChoice = found && !lookup.category
    const missing = Boolean(model.trim()) && !searching && (lookup?.status === 'unknown' || lookupFailed)
    // A model already on the shelf is not a warning, it is a different screen. A second
    // unit belongs on the existing listing as a quantity; a new listing would split the
    // model across two product pages and put it beyond seikoEnrich's reach, which refuses
    // to touch any model holding stock on more than one listing.
    const blocked = inStock.length > 0
    const canSave = !blocked
        && ((found && (!needsCategoryChoice || fallback.category)) || (missing && confirmedUnknown))

    function handleEntryChange(event) {
        const { id, value } = event.target
        setEntry(prev => ({ ...prev, [id]: value }))
    }

    async function handleReport() {
        // Fire-and-forget by design: the server hands this to Discord, which swallows its
        // own failures. A clerk who has already been blocked should not then be blocked
        // again by a failed report.
        await fetchResource({
            resource: 'products/seiko-report',
            body: { model: model.trim().toUpperCase(), existing: inStock, note: reportNote },
        })
        setReported(true)
    }

    async function handleSubmit(event) {
        event.preventDefault()

        // A form submits on Enter in any text field, with or without a Save button. Without
        // this, the duplicate block would be a button that is merely hidden rather than a
        // rule that holds -- and a clerk hitting Enter out of habit would create the very
        // second listing the block exists to prevent.
        if (!canSave) return

        if (parseFloat(entry.cost) >= parseFloat(entry.price)) {
            return alert(t('products.costExceedsPriceError', {
                cost: parseFloat(entry.cost), price: parseFloat(entry.price),
            }))
        }

        const typed = model.trim().toUpperCase()

        const fields = found
            ? {
                title: lookup.title,
                // NOT dead code -- this was simplified to `lookup.category` once already
                // (it looked unreachable) and that silently saved product_type: null for
                // SSH137/SSH139, the two found records with no gender on file. Leave the
                // fallback: it's the clerk's picker answer for exactly that case.
                category: lookup.category ?? fallback.category,
                vendor: 'Seiko',
                imageUrl: lookup.imageUrl ?? null,
                ...entry,
            }
            : {
                title: `${fallback.name} ${fallback.category.replace(/es$/, '')}`.trim(),
                category: fallback.category,
                vendor: 'Seiko',
                seikoUnknownModel: typed,
                ...entry,
            }

        const body = {
            fields,
            metafields: {
                name: { value: found ? '' : fallback.name, id: '' },
                watch_model: { value: found ? lookup.model : typed, id: '' },
            },
        }

        // Only a found model carries a seiko block. Its absence is what tells the server
        // to skip phase two entirely.
        if (found) body.seiko = { model: lookup.model, contentHash: lookup.contentHash }

        await runBlocking(async () => {
            const response = await fetchResource({ resource: 'products/create', body })
            if (response.result !== 'OK') return alert(t('products.seiko.saveError'))
            setShowAddSeiko(false)
            await refreshProducts()
        })
    }

    if (!showAddSeiko) return null

    return (
        <FormPage
            title={t('products.seiko.title')}
            onSubmit={handleSubmit}
            measure={FORM_MEASURE}
            actions={
                <ActionBar
                    cancel={{ label: t('common.cancel'), onClick: () => setShowAddSeiko(false) }}
                    // Absent, not disabled, when the model is already in stock. A greyed-out
                    // Save that can never become enabled reads as something to keep tapping.
                    confirm={blocked
                        ? null
                        : { label: t('common.save'), type: 'submit', disabled: !canSave }}
                />
            }
        >
            <Grid container spacing={2} justifyContent='center' id='add-seiko-watch'>
                <Grid item xs={12}>
                    <TextField
                        required
                        autoFocus
                        fullWidth
                        autoComplete='off'
                        id='seiko-model'
                        label={t('products.seiko.modelNumber')}
                        value={model}
                        onChange={event => setModel(event.target.value)}
                        helperText={searching ? t('products.seiko.searching') : ' '}
                    />
                </Grid>

                {blocked && (
                    <Grid item xs={12}>
                        <Alert severity='warning'>
                            <AlertTitle>
                                {t('products.seiko.alreadyInStock', { count: inStock.length })}
                            </AlertTitle>
                            {inStock.map(match => (
                                <Box key={match.id} sx={{ mt: 1 }}>
                                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                                        {match.title}
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        {/* `products.inStock` is "{{count}} in stock" and needs
                                            its count -- passing none renders the placeholder raw. */}
                                        {match.quantity === null
                                            ? t('products.seiko.qtyUntracked')
                                            : t('products.inStock', { count: match.quantity })}
                                        {match.barcode ? `  ·  ${match.barcode}` : ''}
                                    </Typography>
                                    {match.barcode && onExistingPiece && (
                                        <Button
                                            size='small'
                                            variant='outlined'
                                            sx={{ mt: 1 }}
                                            onClick={() => onExistingPiece(match.barcode)}
                                        >
                                            {t('products.seiko.openPiece')}
                                        </Button>
                                    )}
                                </Box>
                            ))}

                            <Typography variant='body2' sx={{ mt: 2 }}>
                                {t('products.seiko.alreadyInStockHelp')}
                            </Typography>

                            {reported ? (
                                <Typography variant='body2' sx={{ mt: 2, fontWeight: 600 }}>
                                    {t('products.seiko.reportSent')}
                                </Typography>
                            ) : reporting ? (
                                <Box sx={{ mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        size='small'
                                        id='seiko-report-note'
                                        label={t('products.seiko.reportNotePrompt')}
                                        value={reportNote}
                                        onChange={event => setReportNote(event.target.value)}
                                    />
                                    <Button size='small' sx={{ mt: 1 }} onClick={handleReport}>
                                        {t('products.seiko.reportSend')}
                                    </Button>
                                </Box>
                            ) : (
                                <Button size='small' sx={{ mt: 2 }} onClick={() => setReporting(true)}>
                                    {t('products.seiko.reportData')}
                                </Button>
                            )}
                        </Alert>
                    </Grid>
                )}

                {found && !blocked && (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            {lookup.imageUrl ? (
                                // Tappable. The thumbnail is 120px, which is enough to tell
                                // a watch from a different watch and not enough to check a
                                // dial against the one in your hand -- which is the whole
                                // job this card is here to do.
                                <Box
                                    component='img'
                                    src={lookup.imageUrl}
                                    alt={lookup.imageAlt || lookup.title}
                                    onClick={() => setPhotoOpen(true)}
                                    sx={{
                                        width: 120, height: 120, objectFit: 'contain', flexShrink: 0,
                                        cursor: 'zoom-in',
                                    }}
                                />
                            ) : (
                                <Box sx={{
                                    width: 120, height: 120, flexShrink: 0, display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', textAlign: 'center',
                                    border: '1px dashed', borderColor: 'divider', p: 1,
                                }}>
                                    <Typography variant='caption' color='text.secondary'>
                                        {t('products.seiko.noImage')}
                                    </Typography>
                                </Box>
                            )}
                            <Box>
                                <Typography variant='subtitle1'>{lookup.title}</Typography>
                                <Typography variant='body2' color='text.secondary' data-testid='seiko-identity'>
                                    {[
                                        lookup.identity.caseDiameterMm && `${lookup.identity.caseDiameterMm}mm`,
                                        lookup.identity.dialColor && `${lookup.identity.dialColor} dial`,
                                        lookup.identity.movement,
                                    ].filter(Boolean).join('  ·  ')}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Its own block, deliberately. This sentence is the human gate --
                            the one thing only the clerk can verify, standing there with the
                            watch. Sharing a text style with the spec line above made it read
                            as one more fact rather than as a question being asked of them. */}
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant='body1' sx={{ fontWeight: 600 }}>
                                {t('products.seiko.confirm')}
                            </Typography>
                        </Box>

                        <Lightbox
                            open={photoOpen}
                            onClose={() => setPhotoOpen(false)}
                            src={lookup.imageUrl}
                            alt={lookup.imageAlt || lookup.title}
                        />
                    </Grid>
                )}

                {needsCategoryChoice && (
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            blurOnSelect
                            id='seiko-category'
                            value={fallback.category || null}
                            options={WATCH_CATEGORIES}
                            onChange={(event, value) => setFallback(prev => ({ ...prev, category: value ?? '' }))}
                            renderInput={params => (
                                <TextField {...params} required label={t('products.fields.category')} />
                            )}
                        />
                    </Grid>
                )}

                {missing && !blocked && (
                    <Grid item xs={12}>
                        <Alert
                            severity='warning'
                            action={!confirmedUnknown && (
                                <Button color='inherit' size='small' onClick={() => setConfirmedUnknown(true)}>
                                    {t('products.seiko.notFoundContinue')}
                                </Button>
                            )}
                        >
                            {lookupFailed
                                ? t('products.seiko.lookupFailed')
                                : `${t('products.seiko.notFoundTitle', { model: model.trim().toUpperCase() })} ${t('products.seiko.notFoundHelp')}`}
                            {confirmedUnknown && ` ${t('products.seiko.notFoundSaving')}`}
                        </Alert>
                    </Grid>
                )}

                {missing && confirmedUnknown && !blocked && (
                    <>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                blurOnSelect
                                id='seiko-category'
                                value={fallback.category || null}
                                options={WATCH_CATEGORIES}
                                onChange={(event, value) => setFallback(prev => ({ ...prev, category: value ?? '' }))}
                                renderInput={params => (
                                    <TextField {...params} required label={t('products.fields.category')} />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required
                                fullWidth
                                autoComplete='off'
                                id='seiko-name'
                                label={t('products.fields.title')}
                                value={fallback.name}
                                onChange={event => setFallback(prev => ({
                                    ...prev, name: capitalizeFirstLetters(event.target.value),
                                }))}
                            />
                        </Grid>
                    </>
                )}

                {canSave && (
                    <>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required fullWidth type='number' autoComplete='off'
                                id='cost' label={t('products.fields.cost')}
                                value={entry.cost} onChange={handleEntryChange}
                                InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required fullWidth type='number' autoComplete='off'
                                id='price' label={t('products.fields.salePrice')}
                                value={entry.price} onChange={handleEntryChange}
                                InputProps={{ startAdornment: <InputAdornment position='start'>$</InputAdornment> }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth type='number' id='nwk_quantity'
                                label={t('products.fields.newarkQuantity')}
                                value={entry.nwk_quantity} onChange={handleEntryChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth type='number' id='lb_quantity'
                                label={t('products.fields.longBranchQuantity')}
                                value={entry.lb_quantity} onChange={handleEntryChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                required fullWidth type='number' autoComplete='off'
                                id='weight' label={t('products.fields.weight')}
                                value={entry.weight} onChange={handleEntryChange}
                                InputProps={{ endAdornment: <InputAdornment position='end'>g</InputAdornment> }}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </FormPage>
    )
}
